/**
 * Test helpers for runtime services
 */

import { vi } from 'vitest'
import type { Knex } from 'knex'
import type {
  ModuleContext,
  CollectionEntityDefinition,
  SingleEntityDefinition,
  EventEntityDefinition,
  ActionDefinition,
  // ConfigEntityDefinition absorbed into SingleEntityDefinition
  // TempEntityDefinition absorbed into CollectionEntityDefinition
  ViewEntityDefinition,
  ExternalEntityDefinition,
  ComputedEntityDefinition,
  TreeEntityDefinition,
  DagEntityDefinition,
  DatabaseAdapter
} from '@gzl10/nexus-sdk'
import { createInMemoryAdapter } from '@kernel/db/memory-adapter.js'
import { ManagedCacheImpl } from '@kernel/cache/managed-cache.js'
import { applySearchFilter, getSearchableFields, getPagination, buildPaginatedResult } from '@kernel/db/query-helpers.js'
import { ErrorCodes } from '../core/errors/error-codes.js'

// Mock error classes
class AppError extends Error {
  constructor(message: string, public statusCode: number = 500) {
    super(message)
    this.name = 'AppError'
  }
}

class NotFoundError extends Error {
  constructor(resource = 'Resource') {
    super(`${resource} not found`)
    this.name = 'NotFoundError'
  }
}

class UnauthorizedError extends Error {
  constructor(message = 'Unauthorized') {
    super(message)
    this.name = 'UnauthorizedError'
  }
}

class ForbiddenError extends Error {
  constructor(message = 'Forbidden') {
    super(message)
    this.name = 'ForbiddenError'
  }
}

class ConflictError extends Error {
  constructor(message = 'Conflict') {
    super(message)
    this.name = 'ConflictError'
  }
}

class ValidationError extends Error {
  public details: Array<{ path: string; message: string; code?: string; interpolation?: Record<string, string | number> }>

  constructor(message = 'Validation failed', details: Array<{ path: string; message: string; code?: string; interpolation?: Record<string, string | number> }> = []) {
    super(message)
    this.name = 'ValidationError'
    this.details = details
  }
}

interface MockQueryBuilderState {
  _data: unknown[]
  _whereClauses: Array<{ column: string; value: unknown; operator?: string }>
  _whereInClauses: Array<{ column: string; values: unknown[] }>
  _whereNotInClauses: Array<{ column: string; values: unknown[] }>
  _whereNotClauses: Array<{ column: string; value: unknown }>
  _whereNullClauses: string[]
  _whereNotNullClauses: string[]
  _orderBy: { column: string; direction: string } | null
  _limit: number | null
  _offset: number | null
  _insertData: unknown
  _updateData: unknown
  _deleted: boolean
  where: (column: string, operatorOrValue: unknown, value?: unknown) => MockQueryBuilderState
  whereNot: (column: string, value: unknown) => MockQueryBuilderState
  whereIn: (column: string, values: unknown[]) => MockQueryBuilderState
  whereNotIn: (column: string, values: unknown[]) => MockQueryBuilderState
  whereNull: (column: string) => MockQueryBuilderState
  whereNotNull: (column: string) => MockQueryBuilderState
  whereBetween: () => MockQueryBuilderState
  orderBy: (column: string, direction?: string) => MockQueryBuilderState
  limit: (n: number) => MockQueryBuilderState
  offset: (n: number) => MockQueryBuilderState
  select: () => MockQueryBuilderState
  join: () => MockQueryBuilderState
  leftJoin: () => MockQueryBuilderState
  groupBy: () => MockQueryBuilderState
  andWhere: () => MockQueryBuilderState
  orWhere: () => MockQueryBuilderState
  returning: () => MockQueryBuilderState
  clone: () => Knex.QueryBuilder
  count: () => { first: ReturnType<typeof vi.fn> }
  first: () => Promise<unknown>
  insert: (data: unknown) => MockQueryBuilderState
  update: (data: unknown) => MockQueryBuilderState
  delete: () => Promise<number>
  then: (resolve: (value: unknown) => void) => void
  [key: string]: unknown
}

/**
 * Create mock Knex query builder
 */
export function createMockQueryBuilder(data: unknown[] = []) {
  const mockQb: MockQueryBuilderState = {
    _data: [...data],
    _whereClauses: [] as Array<{ column: string; value: unknown; operator?: string }>,
    _whereInClauses: [] as Array<{ column: string; values: unknown[] }>,
    _whereNotInClauses: [] as Array<{ column: string; values: unknown[] }>,
    _whereNotClauses: [] as Array<{ column: string; value: unknown }>,
    _whereNullClauses: [] as string[],
    _whereNotNullClauses: [] as string[],
    _orderBy: null as { column: string; direction: string } | null,
    _limit: null as number | null,
    _offset: null as number | null,
    _insertData: null as unknown,
    _updateData: null as unknown,
    _deleted: false,

    where(column: string, operatorOrValue: unknown, value?: unknown) {
      if (value !== undefined) {
        // 3-arg form: where(column, operator, value)
        mockQb._whereClauses = [...(mockQb._whereClauses as Array<{ column: string; value: unknown; operator?: string }>), { column, operator: operatorOrValue as string, value }]
      } else {
        // 2-arg form: where(column, value)
        mockQb._whereClauses = [...(mockQb._whereClauses as Array<{ column: string; value: unknown; operator?: string }>), { column, value: operatorOrValue }]
      }
      return mockQb
    },

    whereNot(column: string, value: unknown) {
      mockQb._whereNotClauses = [...(mockQb._whereNotClauses as Array<{ column: string; value: unknown }>), { column, value }]
      return mockQb
    },

    whereIn(column: string, values: unknown[]) {
      mockQb._whereInClauses = [...(mockQb._whereInClauses as Array<{ column: string; values: unknown[] }>), { column, values }]
      return mockQb
    },

    whereNotIn(column: string, values: unknown[]) {
      mockQb._whereNotInClauses = [...(mockQb._whereNotInClauses as Array<{ column: string; values: unknown[] }>), { column, values }]
      return mockQb
    },

    whereNull(column: string) {
      mockQb._whereNullClauses = [...(mockQb._whereNullClauses as string[]), column]
      return mockQb
    },

    whereNotNull(column: string) {
      mockQb._whereNotNullClauses = [...(mockQb._whereNotNullClauses as string[]), column]
      return mockQb
    },

    whereBetween() {
      return mockQb
    },

    orderBy(column: string, direction = 'asc') {
      mockQb._orderBy = { column, direction }
      return mockQb
    },

    limit(n: number) {
      mockQb._limit = n
      return mockQb
    },

    offset(n: number) {
      mockQb._offset = n
      return mockQb
    },

    select() {
      return mockQb
    },

    join() {
      return mockQb
    },

    leftJoin() {
      return mockQb
    },

    groupBy() {
      return mockQb
    },

    andWhere() {
      return mockQb
    },

    orWhere() {
      return mockQb
    },

    returning() {
      return mockQb
    },

    clone() {
      return createMockQueryBuilder(mockQb._data as unknown[])
    },

    count() {
      return {
        first: vi.fn().mockResolvedValue({ count: (mockQb._data as unknown[]).length })
      }
    },

    async first() {
      let result = applyFilters(mockQb._data as Record<string, unknown>[], mockQb)

      // Apply orderBy if set
      if (mockQb._orderBy) {
        const { column, direction } = mockQb._orderBy as { column: string; direction: string }
        result = [...result].sort((a, b) => {
          const aVal = String(a[column] ?? '')
          const bVal = String(b[column] ?? '')
          return direction === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal)
        })
      }

      return result[0] ?? undefined
    },

    insert(data: unknown) {
      mockQb._insertData = data
      return mockQb
    },

    update(data: unknown) {
      mockQb._updateData = data
      return mockQb
    },

    async delete() {
      mockQb._deleted = true
      return 1
    },

    then(resolve: (value: unknown) => void) {
      // Handle insert: return inserted data (or [1] as legacy sentinel for auto-id extraction)
      if (mockQb._insertData !== null) {
        resolve([mockQb._insertData])
        return
      }
      // Handle update: return updated data (mirrors Knex .returning('*') behavior)
      if (mockQb._updateData !== null) {
        const filtered = applyFilters(mockQb._data as Record<string, unknown>[], mockQb)
        resolve(filtered)
        return
      }
      const filtered = applyFilters(mockQb._data as Record<string, unknown>[], mockQb)
      let result = filtered

      if (mockQb._orderBy) {
        const { column, direction } = mockQb._orderBy as { column: string; direction: string }
        result = [...result].sort((a, b) => {
          const aVal = String(a[column] ?? '')
          const bVal = String(b[column] ?? '')
          return direction === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal)
        })
      }

      if (mockQb._offset !== null) {
        result = result.slice(mockQb._offset as number)
      }

      if (mockQb._limit !== null) {
        result = result.slice(0, mockQb._limit as number)
      }

      resolve(result)
    }
  }

  return mockQb as unknown as Knex.QueryBuilder
}

function applyFilters(data: Record<string, unknown>[], qb: MockQueryBuilderState): Record<string, unknown>[] {
  let result = [...data]

  // Apply where clauses (supports 2-arg equality and 3-arg operator forms)
  for (const clause of qb._whereClauses as Array<{ column: string; value: unknown; operator?: string }>) {
    if (clause.operator) {
      // 3-arg form: where(column, operator, value)
      result = result.filter(item => {
        const itemVal = item[clause.column]
        switch (clause.operator) {
          case '>': return (itemVal as number) > (clause.value as number)
          case '>=': return (itemVal as number) >= (clause.value as number)
          case '<': return (itemVal as number) < (clause.value as number)
          case '<=': return (itemVal as number) <= (clause.value as number)
          case 'like': {
            const pattern = String(clause.value)
            const str = String(itemVal ?? '')
            if (pattern.startsWith('%') && pattern.endsWith('%'))
              return str.toLowerCase().includes(pattern.slice(1, -1).toLowerCase())
            if (pattern.startsWith('%'))
              return str.toLowerCase().endsWith(pattern.slice(1).toLowerCase())
            if (pattern.endsWith('%'))
              return str.toLowerCase().startsWith(pattern.slice(0, -1).toLowerCase())
            return str === pattern
          }
          default: return itemVal === clause.value
        }
      })
    } else {
      // 2-arg form: where(column, value) — equality
      result = result.filter(item => item[clause.column] === clause.value)
    }
  }

  // Apply whereNot clauses
  for (const clause of qb._whereNotClauses as Array<{ column: string; value: unknown }>) {
    result = result.filter(item => item[clause.column] !== clause.value)
  }

  // Apply whereIn clauses
  for (const clause of qb._whereInClauses as Array<{ column: string; values: unknown[] }>) {
    result = result.filter(item => clause.values.includes(item[clause.column]))
  }

  // Apply whereNotIn clauses
  for (const clause of qb._whereNotInClauses as Array<{ column: string; values: unknown[] }>) {
    result = result.filter(item => !clause.values.includes(item[clause.column]))
  }

  // Apply whereNull clauses
  for (const column of qb._whereNullClauses as string[]) {
    result = result.filter(item => item[column] === null || item[column] === undefined)
  }

  // Apply whereNotNull clauses
  for (const column of qb._whereNotNullClauses as string[]) {
    result = result.filter(item => item[column] !== null && item[column] !== undefined)
  }

  return result
}

/**
 * Create mock Knex instance
 */
export function createMockDb(tableData: Record<string, unknown[]> = {}) {
  const mockDb = vi.fn((table: string) => {
    return createMockQueryBuilder(tableData[table] ?? [])
  }) as unknown as Knex

  // Add schema property for migrations
  ;(mockDb as unknown as Record<string, unknown>)['schema'] = {
    hasTable: vi.fn().mockResolvedValue(false),
    createTable: vi.fn().mockResolvedValue(undefined)
  }

  // Add raw method
  ;(mockDb as unknown as Record<string, unknown>)['raw'] = vi.fn().mockReturnValue({
    then: (resolve: (v: unknown) => void) => resolve([])
  })

  // Add transaction method
  ;(mockDb as unknown as Record<string, unknown>)['transaction'] = vi.fn().mockImplementation(async (callback: (trx: unknown) => Promise<void>) => {
    // Create a trx mock that works like the db mock
    const trxMock = vi.fn((table: string) => {
      return createMockQueryBuilder(tableData[table] ?? [])
    })
    await callback(trxMock)
  })

  return mockDb
}

/**
 * Create mock DbContext with the new structure.
 * Wraps a Knex mock in the full DbContext interface.
 */
export function createMockDbContext(tableData: Record<string, unknown[]> = {}): ModuleContext['db'] {
  const mockKnex = createMockDb(tableData)

  return {
    knex: mockKnex,
    adapter: {
      findMany: vi.fn().mockResolvedValue({ items: [], total: 0, page: 1, limit: 20, totalPages: 0, hasNext: false }),
      findOne: vi.fn().mockResolvedValue(null),
      findById: vi.fn().mockResolvedValue(null),
      count: vi.fn().mockResolvedValue(0),
      insert: vi.fn().mockResolvedValue({}),
      update: vi.fn().mockResolvedValue({}),
      delete: vi.fn().mockResolvedValue(true),
      transaction: vi.fn().mockImplementation(async (fn: (adapter: unknown) => Promise<void>) => fn({}))
    },
    schema: {
      hasTable: vi.fn().mockResolvedValue(false),
      hasColumn: vi.fn().mockResolvedValue(false),
      getColumns: vi.fn().mockResolvedValue([]),
      createTable: vi.fn().mockResolvedValue(undefined),
      alterTable: vi.fn().mockResolvedValue(undefined),
      dropTable: vi.fn().mockResolvedValue(undefined),
      addTimestamps: vi.fn().mockResolvedValue(undefined),
      addAuditFields: vi.fn().mockResolvedValue(undefined),
      addColumnIfMissing: vi.fn().mockResolvedValue(false),
      raw: {}
    },
    t: (name: string) => name,
    addTimestamps: vi.fn(),
    addAuditFieldsIfMissing: vi.fn(),
    addSoftDeleteFieldIfMissing: vi.fn(),
    addConfigDefaultField: vi.fn(),
    addColumnIfMissing: vi.fn(),
    nowTimestamp: () => new Date().toISOString(),
    formatTimestamp: (_db: unknown, date?: Date) => (date ?? new Date()).toISOString(),
    applySearchFilter,
    getPagination,
    buildPaginatedResult,
    getSearchableFields
  } as unknown as ModuleContext['db']
}

/**
 * Options for createMockContext, extending ModuleContext overrides with
 * plugin-specific helpers.
 */
interface MockContextOptions extends Partial<ModuleContext> {
  pluginCode?: string
}

/**
 * Create mock ModuleContext
 * Provides sensible defaults for all context properties.
 * Use overrides to customize specific properties per test.
 *
 * Structure matches the new ModuleContext interface:
 * - ctx.core: logger, errors, generateId, crypto, socket, etc.
 * - ctx.db: knex, adapter, schema, helper functions
 * - ctx.engine: module/plugin introspection
 * - ctx.services: inter-module service registry
 * - ctx.adapters: external data source adapters
 *
 * @param overrides - Partial ModuleContext overrides plus optional pluginCode
 *   for table name prefixing via ctx.db.t()
 */
export function createMockContext(overrides: MockContextOptions = {}): ModuleContext {
  let idCounter = 0

  // Extract pluginCode before spreading into ModuleContext
  const { pluginCode, ...rest } = overrides as MockContextOptions
  const t = pluginCode
    ? (name: string) => `${pluginCode}_${name}`
    : (name: string) => name

  // Service registry (private, exposed via API)
  const services: Record<string, unknown> = {}

  // Adapter registry (private, exposed via API)
  // Pre-register 'temp' adapter with InMemoryAdapter for TempService tests
  const tempAdapter = createInMemoryAdapter()
  const adapters: Record<string, { data: unknown; schema?: unknown }> = {
    temp: { data: tempAdapter }
  }

  // Handle legacy override pattern: if overrides.db is a function (Knex mock),
  // wrap it in the new DbContext structure for backwards compatibility
  let processedOverrides = { ...rest }
  if (rest.db && typeof rest.db === 'function') {
    // It's a raw Knex mock, wrap it in DbContext structure
    const knexMock = rest.db as unknown as Knex
    processedOverrides = {
      ...rest,
      db: {
        knex: knexMock,
        adapter: {
          findMany: vi.fn().mockResolvedValue({ items: [], total: 0, page: 1, limit: 20, totalPages: 0, hasNext: false }),
          findOne: vi.fn().mockResolvedValue(null),
          findById: vi.fn().mockResolvedValue(null),
          count: vi.fn().mockResolvedValue(0),
          insert: vi.fn().mockResolvedValue({}),
          update: vi.fn().mockResolvedValue({}),
          delete: vi.fn().mockResolvedValue(true),
          transaction: vi.fn().mockImplementation(async (fn: (adapter: unknown) => Promise<void>) => fn({}))
        },
        schema: {
          hasTable: vi.fn().mockResolvedValue(false),
          hasColumn: vi.fn().mockResolvedValue(false),
          getColumns: vi.fn().mockResolvedValue([]),
          createTable: vi.fn().mockResolvedValue(undefined),
          alterTable: vi.fn().mockResolvedValue(undefined),
          dropTable: vi.fn().mockResolvedValue(undefined),
          addTimestamps: vi.fn().mockResolvedValue(undefined),
          addAuditFields: vi.fn().mockResolvedValue(undefined),
          addColumnIfMissing: vi.fn().mockResolvedValue(false),
          raw: {}
        },
        t,
        addTimestamps: vi.fn(),
        addAuditFieldsIfMissing: vi.fn(),
        addSoftDeleteFieldIfMissing: vi.fn(),
        addConfigDefaultField: vi.fn(),
        addColumnIfMissing: vi.fn(),
        nowTimestamp: () => new Date().toISOString(),
        formatTimestamp: (_db: unknown, date?: Date) => (date ?? new Date()).toISOString(),
        applySearchFilter,
        getPagination,
        buildPaginatedResult,
        getSearchableFields,
        getKnex: () => knexMock,
        // Legacy: ctx.db.raw → now ctx.db.knex.raw
        raw: (knexMock as unknown as { raw: unknown }).raw
      } as unknown as ModuleContext['db']
    }
  }

  // Mock Knex instance
  const mockKnex = createMockDb()

  // Mock logger
  const mockLogger = {
    info: vi.fn(),
    warn: vi.fn(),
    error: vi.fn(),
    debug: vi.fn(),
    trace: vi.fn(),
    fatal: vi.fn(),
    child: vi.fn().mockReturnThis()
  }

  // Mock schema adapter
  const mockSchemaAdapter = {
    hasTable: vi.fn().mockResolvedValue(false),
    hasColumn: vi.fn().mockResolvedValue(false),
    getColumns: vi.fn().mockResolvedValue([]),
    createTable: vi.fn().mockResolvedValue(undefined),
    alterTable: vi.fn().mockResolvedValue(undefined),
    dropTable: vi.fn().mockResolvedValue(undefined),
    addTimestamps: vi.fn().mockResolvedValue(undefined),
    addAuditFields: vi.fn().mockResolvedValue(undefined),
    addColumnIfMissing: vi.fn().mockResolvedValue(false),
    raw: {}
  }

  // Mock database adapter
  const mockDbAdapter = {
    findMany: vi.fn().mockResolvedValue({ items: [], total: 0, page: 1, limit: 20, totalPages: 0, hasNext: false }),
    findOne: vi.fn().mockResolvedValue(null),
    findById: vi.fn().mockResolvedValue(null),
    count: vi.fn().mockResolvedValue(0),
    insert: vi.fn().mockResolvedValue({}),
    update: vi.fn().mockResolvedValue({}),
    delete: vi.fn().mockResolvedValue(true),
    transaction: vi.fn().mockImplementation(async (fn: (adapter: unknown) => Promise<void>) => fn({}))
  }

  return {
    // =========================================================================
    // Core Context: logger, errors, crypto, socket, events, middleware
    // =========================================================================
    core: {
      logger: mockLogger as unknown as ModuleContext['core']['logger'],
      errors: {
        AppError,
        NotFoundError,
        UnauthorizedError,
        ForbiddenError,
        ConflictError,
        ValidationError,
        codes: ErrorCodes
      },
      crypto: {
        hashPassword: vi.fn().mockResolvedValue('$2b$12$mockhash'),
        verifyPassword: vi.fn().mockResolvedValue(true),
        DUMMY_HASH: '$2b$12$dummyhashfordummypasswordcheck'
      },
      socket: {
        getIO: vi.fn().mockReturnValue({}),
        isInitialized: vi.fn().mockReturnValue(false),
        isUserConnected: vi.fn().mockReturnValue(false),
        getUserSocketCount: vi.fn().mockReturnValue(0),
        getConnectedUsers: vi.fn().mockReturnValue([])
      },
      sse: {
        sendEvent: vi.fn(),
        close: vi.fn()
      },
      abilities: {} as ModuleContext['core']['abilities'],
      events: {
        emit: vi.fn(),
        emitEvent: vi.fn(),
        emitAsync: vi.fn().mockResolvedValue(undefined),
        on: vi.fn(),
        off: vi.fn()
      },
      middleware: {} as ModuleContext['core']['middleware'],
      createRouter: vi.fn(),
      generateId: () => `test-id-${++idCounter}`,
      generateIdByType: (type?: string) => {
        if (type === 'auto' || type === 'custom' || type === 'pattern') return undefined
        return `test-id-${++idCounter}`
      },
      getLibPath: () => '/tmp',
      getProjectPath: () => '/tmp',
      cache: (() => {
        const caches = new Map<string, ManagedCacheImpl<any>>()
        return {
          create: vi.fn().mockImplementation((name: string, opts?: any) => {
            const cache = new ManagedCacheImpl(name, opts)
            caches.set(name, cache)
            return cache
          }),
          getOrCreate: vi.fn().mockImplementation((name: string, opts?: any) => {
            if (caches.has(name)) return caches.get(name)!
            const cache = new ManagedCacheImpl(name, opts)
            caches.set(name, cache)
            return cache
          }),
          get: vi.fn().mockImplementation((name: string) => caches.get(name)),
          clearAll: vi.fn(),
          stats: vi.fn().mockReturnValue({}),
          destroy: vi.fn()
        }
      })(),
      safeJsonParse: <T>(jsonString: string, fallback: T): T => {
        try {
          return JSON.parse(jsonString) as T
        } catch {
          return fallback
        }
      }
    } as unknown as ModuleContext['core'],

    // =========================================================================
    // Database Context: knex, adapter, schema, helper functions
    // =========================================================================
    db: {
      knex: mockKnex,
      adapter: mockDbAdapter as unknown as ModuleContext['db']['adapter'],
      schema: mockSchemaAdapter as unknown as ModuleContext['db']['schema'],
      t,
      addTimestamps: vi.fn(),
      addAuditFieldsIfMissing: vi.fn(),
      addSoftDeleteFieldIfMissing: vi.fn(),
      addConfigDefaultField: vi.fn(),
      addColumnIfMissing: vi.fn(),
      nowTimestamp: () => new Date().toISOString(),
      formatTimestamp: (_db: unknown, date?: Date) => (date ?? new Date()).toISOString(),
      applySearchFilter,
      getPagination,
      buildPaginatedResult,
      getSearchableFields,
      getKnex: () => mockKnex,
      // Legacy: ctx.db.raw → now ctx.db.knex.raw
      raw: (mockKnex as unknown as { raw: unknown }).raw
    } as unknown as ModuleContext['db'],

    // =========================================================================
    // Runtime Context: entity service/controller/router factories
    // =========================================================================
    runtime: {
      createEntityService: vi.fn(),
      createEntityController: vi.fn(),
      createEntityRouter: vi.fn()
    } as unknown as ModuleContext['runtime'],

    // =========================================================================
    // Config Context: env vars and resolved NexusConfig
    // =========================================================================
    config: {} as ModuleContext['config'],

    // =========================================================================
    // Engine Context: module/plugin introspection
    // =========================================================================
    engine: {
      getModules: vi.fn().mockReturnValue([]),
      getPlugins: vi.fn().mockReturnValue([]),
      getModuleSubjects: vi.fn().mockReturnValue([]),
      getRegisteredSubjects: vi.fn().mockReturnValue([])
    } as unknown as ModuleContext['engine'],

    // =========================================================================
    // Services Context: inter-module service registry
    // =========================================================================
    services: {
      register(name: string, service: unknown): void {
        services[name] = service
      },
      get<T>(serviceName: string): T {
        const service = services[serviceName]
        if (!service) {
          throw new Error(`Service "${serviceName}" not initialized.`)
        }
        return service as T
      },
      getOptional<T>(serviceName: string): T | undefined {
        return services[serviceName] as T | undefined
      },
      has(serviceName: string): boolean {
        return !!services[serviceName]
      }
    } as unknown as ModuleContext['services'],

    // =========================================================================
    // Adapters Context: external data source adapters
    // =========================================================================
    adapters: {
      register(name: string, dataAdapter: unknown, schemaAdapter?: unknown): void {
        adapters[name] = { data: dataAdapter, schema: schemaAdapter }
      },
      get(name: string) {
        const pair = adapters[name]
        if (!pair) {
          throw new Error(`Adapter "${name}" not found`)
        }
        return pair.data
      },
      getSchema(name: string) {
        const pair = adapters[name]
        return pair?.schema
      },
      has(name: string): boolean {
        return name in adapters
      }
    } as unknown as ModuleContext['adapters'],

    // =========================================================================
    // Root-level shortcut
    // =========================================================================
    createRouter: vi.fn(),

    // Semantic events API (NEX-161)
    events: {
      notify: vi.fn(),
      query: vi.fn().mockResolvedValue([]),
      command: vi.fn().mockResolvedValue(undefined),
      on: vi.fn(),
      off: vi.fn(),
      once: vi.fn(),
      onAny: vi.fn(),
    },

    // =========================================================================
    // Legacy API compatibility - deprecated, use new API instead
    // =========================================================================

    // Legacy: ctx.logger → now ctx.core.logger
    logger: mockLogger as unknown as ModuleContext['core']['logger'],

    // Legacy: ctx.errors → now ctx.core.errors
    errors: {
      AppError,
      NotFoundError,
      UnauthorizedError,
      ForbiddenError,
      ConflictError,
      ValidationError,
      codes: ErrorCodes
    },

    // Legacy: ctx.registerService → now ctx.services.register
    registerService(name: string, service: unknown): void {
      services[name] = service
    },
    getService<T>(serviceName: string): T {
      const service = services[serviceName]
      if (!service) {
        throw new Error(`Service "${serviceName}" not initialized.`)
      }
      return service as T
    },
    getOptionalService<T>(serviceName: string): T | undefined {
      return services[serviceName] as T | undefined
    },
    hasService(serviceName: string): boolean {
      return !!services[serviceName]
    },
    registerAdapter(name: string, dataAdapter: unknown, schemaAdapter?: unknown): void {
      adapters[name] = { data: dataAdapter, schema: schemaAdapter }
    },
    getAdapter(name: string) {
      const pair = adapters[name]
      if (!pair) {
        throw new Error(`Adapter "${name}" not found`)
      }
      return pair.data
    },
    hasAdapter(name: string): boolean {
      return name in adapters
    },

    ...processedOverrides
  } as ModuleContext
}

// === Entity Definition Factories ===

export function createCollectionDefinition(
  overrides: Partial<CollectionEntityDefinition> = {}
): CollectionEntityDefinition {
  return {
    type: 'collection',
    table: 'test_items',
    label: 'Test Items',
    labelField: 'name',
    timestamps: true,
    fields: {
      id: {
        label: 'ID',
        input: 'text',
        hidden: true,
        db: { type: 'string', size: 26, nullable: false }
      },
      name: {
        label: 'Name',
        input: 'text',
        db: { type: 'string', size: 100, nullable: false }
      }
    },
    ...overrides
  }
}

export function createSingleDefinition(
  overrides: Partial<SingleEntityDefinition> = {}
): SingleEntityDefinition {
  return {
    type: 'single',
    key: 'test_settings',
    label: 'Test Settings',
    defaults: { theme: 'light', language: 'en' },
    fields: {
      theme: {
        label: 'Theme',
        input: 'select',
        db: { type: 'string', size: 20, nullable: false }
      },
      language: {
        label: 'Language',
        input: 'select',
        db: { type: 'string', size: 10, nullable: false }
      }
    },
    ...overrides
  }
}

export function createReferenceDefinition(
  overrides: Partial<CollectionEntityDefinition> = {}
): CollectionEntityDefinition {
  return {
    type: 'collection',
    table: 'ref_countries',
    label: 'Countries',
    labelField: 'name',
    seed: [
      { id: 'ES', code: 'ES', name: 'Spain' },
      { id: 'US', code: 'US', name: 'United States' },
      { id: 'MX', code: 'MX', name: 'Mexico' }
    ],
    fields: {
      id: {
        label: 'ID',
        input: 'text',
        hidden: true,
        db: { type: 'string', size: 10, nullable: false }
      },
      code: {
        label: 'Code',
        input: 'text',
        db: { type: 'string', size: 10, nullable: false }
      },
      name: {
        label: 'Name',
        input: 'text',
        db: { type: 'string', size: 100, nullable: false }
      }
    },
    ...overrides
  }
}

export function createEventDefinition(
  overrides: Partial<EventEntityDefinition> = {}
): EventEntityDefinition {
  return {
    type: 'event',
    table: 'evt_audit_logs',
    label: 'Audit Logs',
    labelField: 'action',
    immutable: true,
    defaultSort: { field: 'created_at', order: 'desc' as const },
    retention: { days: 90, maxRows: 10000 },
    fields: {
      id: {
        label: 'ID',
        input: 'text',
        hidden: true,
        db: { type: 'string', size: 26, nullable: false }
      },
      action: {
        label: 'Action',
        input: 'text',
        db: { type: 'string', size: 50, nullable: false }
      },
      user_id: {
        label: 'User',
        input: 'text',
        db: { type: 'string', size: 26, nullable: true }
      },
      created_at: {
        label: 'Created At',
        input: 'datetime',
        db: { type: 'datetime', nullable: false }
      }
    },
    ...overrides
  }
}

/** @deprecated Config absorbed into Single. Use createSingleDefinition with scopeField instead. */
export function createConfigDefinition(
  overrides: Partial<SingleEntityDefinition> = {}
): SingleEntityDefinition {
  return {
    type: 'config',
    key: 'cfg_module_settings',
    label: 'Module Settings',
    scopeField: 'module_name',
    defaults: { enabled: true, maxItems: 100 },
    fields: {
      module_name: {
        label: 'Module',
        input: 'text',
        db: { type: 'string', size: 50, nullable: false }
      },
      enabled: {
        label: 'Enabled',
        input: 'checkbox',
        db: { type: 'boolean', nullable: false }
      },
      maxItems: {
        label: 'Max Items',
        input: 'number',
        db: { type: 'integer', nullable: false }
      }
    },
    ...overrides
  }
}

/** @deprecated Temp absorbed into Collection. Use createCollectionDefinition with retention instead. */
export function createTempDefinition(
  overrides: Partial<CollectionEntityDefinition> = {}
): CollectionEntityDefinition {
  return {
    type: 'temp',
    table: 'tmp_sessions',
    label: 'Sessions',
    labelField: 'token',
    retention: { seconds: 3600, expiresField: 'expires_at' }, // 1 hour
    fields: {
      id: {
        label: 'ID',
        input: 'text',
        hidden: true,
        db: { type: 'string', size: 26, nullable: false }
      },
      token: {
        label: 'Token',
        input: 'text',
        db: { type: 'string', size: 256, nullable: false }
      },
      expires_at: {
        label: 'Expires At',
        input: 'datetime',
        db: { type: 'datetime', nullable: false }
      }
    },
    ...overrides
  }
}

export function createViewDefinition(
  overrides: Partial<ViewEntityDefinition> = {}
): ViewEntityDefinition {
  return {
    type: 'view',
    table: 'vw_user_stats',
    label: 'User Stats',
    labelField: 'user_id',
    query: 'SELECT user_id, COUNT(*) as count FROM posts GROUP BY user_id',
    fields: {
      user_id: {
        label: 'User',
        input: 'text',
        db: { type: 'string', size: 26, nullable: false }
      },
      count: {
        label: 'Count',
        input: 'number',
        db: { type: 'integer', nullable: false }
      }
    },
    ...overrides
  }
}

export function createExternalDefinition(
  overrides: Partial<ExternalEntityDefinition> = {}
): ExternalEntityDefinition {
  return {
    type: 'external',
    adapter: 'test-api',
    table: 'external_items',  // Resource ID in external system
    label: 'External Items',
    labelField: 'title',
    cache: { ttl: 300 },
    fields: {
      id: {
        label: 'ID',
        input: 'text',
        hidden: true,
        db: { type: 'string', size: 26, nullable: false }
      },
      title: {
        label: 'Title',
        input: 'text',
        db: { type: 'string', size: 200, nullable: false }
      }
    },
    ...overrides
  }
}

/** @deprecated Use createComputedDefinition with sources instead */
export function createVirtualDefinition(
  overrides: Partial<ComputedEntityDefinition> = {}
): ComputedEntityDefinition {
  return createComputedDefinition({
    sources: ['source_a', 'source_b'],
    resolver: (sources) => {
      const combined = [
        ...(sources['source_a'] ?? []),
        ...(sources['source_b'] ?? [])
      ]
      return combined
    },
    compute: undefined,
    ...overrides
  })
}

export function createComputedDefinition(
  overrides: Partial<ComputedEntityDefinition> = {}
): ComputedEntityDefinition {
  return {
    type: 'computed',
    label: 'Computed Stats',
    compute: vi.fn().mockResolvedValue([
      { id: '1', metric: 'users', value: 100 },
      { id: '2', metric: 'posts', value: 500 }
    ]),
    cache: { ttl: 60 },
    fields: {
      id: {
        label: 'ID',
        input: 'text',
        hidden: true,
        db: { type: 'string', size: 26, nullable: false }
      },
      metric: {
        label: 'Metric',
        input: 'text',
        db: { type: 'string', size: 50, nullable: false }
      },
      value: {
        label: 'Value',
        input: 'number',
        db: { type: 'integer', nullable: false }
      }
    },
    ...overrides
  }
}

// === Mock DatabaseAdapter Factory ===

/**
 * Creates a mock DatabaseAdapter for testing external services.
 * All methods are vi.fn() mocks with sensible defaults.
 */
export function createMockDatabaseAdapter(overrides: Partial<DatabaseAdapter> = {}): DatabaseAdapter {
  return {
    findMany: vi.fn().mockResolvedValue({
      items: [
        { id: 'ext-1', title: 'External Item 1' },
        { id: 'ext-2', title: 'External Item 2' }
      ],
      total: 2,
      page: 1,
      limit: 20,
      totalPages: 1,
      hasNext: false
    }),
    findOne: vi.fn().mockResolvedValue(null),
    findById: vi.fn().mockResolvedValue({ id: 'ext-1', title: 'External Item 1' }),
    count: vi.fn().mockResolvedValue(2),
    insert: vi.fn().mockResolvedValue({ id: 'ext-new', title: 'Created' }),
    update: vi.fn().mockResolvedValue({ id: 'ext-1', title: 'Updated' }),
    delete: vi.fn().mockResolvedValue(true),
    transaction: vi.fn().mockImplementation(async (fn) => fn({} as DatabaseAdapter)),
    raw: vi.fn().mockResolvedValue([]),
    ...overrides
  }
}

// === Tree and DAG Definition Factories ===

export function createTreeDefinition(
  overrides: Partial<TreeEntityDefinition> = {}
): TreeEntityDefinition {
  return {
    type: 'tree',
    table: 'tree_categories',
    label: 'Categories',
    labelField: 'name',
    timestamps: true,
    seed: [
      { id: 'root', name: 'Root Category', parent_id: null }
    ],
    fields: {
      id: {
        label: 'ID',
        input: 'text',
        hidden: true,
        db: { type: 'string', size: 26, nullable: false }
      },
      name: {
        label: 'Name',
        input: 'text',
        db: { type: 'string', size: 100, nullable: false }
      }
      // parent_id is auto-injected by TreeService
    },
    ...overrides
  }
}

export function createDagDefinition(
  overrides: Partial<DagEntityDefinition> = {}
): DagEntityDefinition {
  return {
    type: 'dag',
    table: 'dag_tags',
    label: 'Tags',
    labelField: 'name',
    timestamps: true,
    seed: [
      { id: 'root', name: 'Root Tag' }
    ],
    fields: {
      id: {
        label: 'ID',
        input: 'text',
        hidden: true,
        db: { type: 'string', size: 26, nullable: false }
      },
      name: {
        label: 'Name',
        input: 'text',
        db: { type: 'string', size: 100, nullable: false }
      }
    },
    ...overrides
  }
}

// === Action Definition Factory ===

export function createActionDefinition(
  overrides: Partial<ActionDefinition> = {}
): ActionDefinition {
  return {
    key: 'test_action',
    label: 'Test Action',
    scope: 'row',
    method: 'POST',
    handler: async () => ({ success: true }),
    ...overrides
  }
}
